import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../services/firebaseConfig";
import {
    collection,
    onSnapshot,
    updateDoc,
    doc,
    getDocs,
    query,
    where,
} from "firebase/firestore";
import { jsPDF } from "jspdf";
import { ORDER_STATUS } from "../../utils/orderStatus";
import "../../styles/OrderListCompiled.css";

const OrderListCompiled = ({
    currentDistrict = null,
    pengerusiName = null,
    userRole = "pengarah",
}) => {
    const [compiledOrders, setCompiledOrders] = useState([]);

    // ✅ Tabs
    const TAB_SENT = "sent"; // ORDER_SENT_TO_HQ
    const TAB_DELIVERED = "delivered"; // HQ_DELIVERED
    const [activeTab, setActiveTab] = useState(TAB_SENT);

    // ✅ Accordion (open row)
    const [openRowId, setOpenRowId] = useState(null);

    useEffect(() => {
        const compiledRef = collection(db, "inventori", "selangor", "compiledOrders");

        const q =
            currentDistrict && userRole === "pengerusi"
                ? query(compiledRef, where("district", "==", currentDistrict))
                : compiledRef;

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            setCompiledOrders(data);
        });

        return () => unsubscribe();
    }, [currentDistrict, userRole]);

    const getUnitPricesFromFirestore = async () => {
        const prices = {};
        const stocksRef = collection(db, "inventori", "selangor", "stocks");
        const snapshot = await getDocs(stocksRef);

        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const itemName = data.name || data.category || "Tidak diketahui";
            prices[itemName] = data.harga; // boleh jadi number atau object
        });

        return prices;
    };

    // Helper untuk ambil harga ikut struktur Firestore
    const getHarga = (productName, variant, role, allPrices) => {
        const hargaData = allPrices[productName];
        if (!hargaData) return 0;

        // Kalau hargaData terus nombor
        if (typeof hargaData === "number") return Number(hargaData || 0);

        // Kalau ada pecahan ikut variant
        if (variant && hargaData?.[variant] && hargaData?.[variant]?.[role] !== undefined) {
            return Number(hargaData[variant][role] || 0);
        }

        // Kalau flat object { kos, pengerusi, pengurus }
        if (hargaData?.[role] !== undefined) {
            return Number(hargaData[role] || 0);
        }

        return 0;
    };

    const formatDateMsMY = (orderDate) => {
        try {
            const d = orderDate?.toDate?.() ? orderDate.toDate() : new Date(orderDate || 0);
            return d.toLocaleString("ms-MY");
        } catch {
            return "-";
        }
    };

    const getItemsSummary = (items) => {
        if (!items || typeof items !== "object") return { categories: 0, products: 0 };

        const categories = Object.keys(items).length;
        let products = 0;

        Object.values(items).forEach((productsObj) => {
            if (productsObj && typeof productsObj === "object") {
                products += Object.keys(productsObj).length;
            }
        });

        return { categories, products };
    };

    const toggleRow = (orderId) => {
        setOpenRowId((prev) => (prev === orderId ? null : orderId));
    };

    const downloadInvoice = async (order) => {
        const pdf = new jsPDF();
        const allPrices = await getUnitPricesFromFirestore();
        const role = "pengerusi";

        const logo = new Image();
        logo.src = "/logo_pasti.png";

        logo.onload = () => {
            pdf.addImage(logo, "PNG", 10, 10, 30, 30);
            pdf.setFontSize(14);
            pdf.text("INVOIS PENGHANTARAN", 50, 20);

            pdf.setFontSize(10);
            pdf.text("Daripada:", 10, 45);
            pdf.text("Pengarah PASTI Selangor", 30, 45);

            pdf.text("Kepada:", 10, 52);
            pdf.text(`${order.pengerusiName || pengerusiName || "-"}`, 30, 52);

            pdf.text("Kawasan:", 10, 59);
            pdf.text(`${order.district || currentDistrict || "-"}`, 30, 59);

            pdf.text(`Status: ${order.status || "-"}`, 10, 66);
            pdf.text(`Tarikh: ${formatDateMsMY(order.orderDate)}`, 10, 73);
            pdf.text(`No. Rujukan: ${order.orderNumber || order.id}`, 10, 80);

            let y = 95;
            pdf.setFontSize(11);
            pdf.text("Bil", 10, y);
            pdf.text("Nama Barang & Butiran", 20, y);
            pdf.text("Harga Seunit", 110, y);
            pdf.text("Kuantiti", 140, y);
            pdf.text("Jumlah (RM)", 170, y);
            y += 8;

            let bil = 1;
            let grandTotal = 0;

            const items = order.items || {};
            Object.entries(items).forEach(([category, products]) => {
                if (!products || typeof products !== "object") return;

                Object.entries(products).forEach(([productName, details]) => {
                    const hargaItem = getHarga(productName, null, role, allPrices);

                    if (details && typeof details === "object") {
                        Object.entries(details).forEach(([variant, qtyObj]) => {
                            let qty = 0;
                            if (qtyObj && typeof qtyObj === "object") {
                                qty = Number(qtyObj.total ?? qtyObj.ordered ?? 0);
                            } else {
                                qty = Number(qtyObj || 0);
                            }

                            const hargaVarian = getHarga(productName, variant, role, allPrices);
                            const jumlah = qty * hargaVarian;

                            pdf.text(`${bil}`, 10, y);
                            const wrappedName = pdf.splitTextToSize(productName, 85);
                            pdf.text(wrappedName, 20, y);
                            pdf.text(`RM ${hargaVarian.toFixed(2)}`, 110, y);
                            pdf.text(`${qty} pcs`, 140, y);
                            pdf.text(`RM ${jumlah.toFixed(2)}`, 170, y);

                            const lineHeight = 5;
                            const nameHeight = wrappedName.length * lineHeight;
                            y += nameHeight;

                            if (y > 285) {
                                pdf.addPage();
                                y = 20;
                            }

                            pdf.setFontSize(10);
                            pdf.text(`${variant}`, 22, y);
                            pdf.setFontSize(11);
                            y += 6;

                            if (y > 285) {
                                pdf.addPage();
                                y = 20;
                            }

                            grandTotal += jumlah;
                            bil++;
                        });
                    } else {
                        const qty = Number(details || 0);
                        const jumlah = qty * hargaItem;

                        pdf.text(`${bil}`, 10, y);
                        pdf.text(productName, 20, y);
                        pdf.text(`RM ${hargaItem.toFixed(2)}`, 110, y);
                        pdf.text(`${qty} pcs`, 140, y);
                        pdf.text(`RM ${jumlah.toFixed(2)}`, 170, y);

                        y += 7;
                        if (y > 285) {
                            pdf.addPage();
                            y = 20;
                        }

                        grandTotal += jumlah;
                        bil++;
                    }
                });
            });

            pdf.setFontSize(12);
            pdf.text(`Jumlah Bayaran Keseluruhan: RM ${grandTotal.toFixed(2)}`, 120, y + 10);
            pdf.save(`Invois_${order.orderNumber || order.id}.pdf`);
        };

        logo.onerror = () => alert("Gagal muatkan logo.");
    };

    const markAsDelivered = async (order) => {
        try {
            // 1) Update compiledOrder status
            const compiledOrderRef = doc(db, "inventori", "selangor", "compiledOrders", order.id);
            await updateDoc(compiledOrderRef, { status: ORDER_STATUS.HQ_DELIVERED });

            // 2) Update HANYA order asal yang termasuk dalam compiledOrder
            const ordersRef = collection(db, "inventori", "selangor", "orders");
            const list = Array.isArray(order.orders) ? order.orders : [];

            if (list.length) {
                const q = query(ordersRef, where("orderNumber", "in", list));
                const snapshot = await getDocs(q);

                await Promise.all(
                    snapshot.docs.map((d) =>
                        updateDoc(doc(db, "inventori", "selangor", "orders", d.id), {
                            status: ORDER_STATUS.HQ_DELIVERED,
                        })
                    )
                );
            }

            alert("Barang telah dihantar kepada pengerusi!");
        } catch (error) {
            console.error("Gagal mengemas kini status:", error);
            alert("Gagal mengemaskini status.");
        }
    };

    // ✅ kira count tab
    const tabCounts = useMemo(() => {
        const sent = compiledOrders.filter((o) => o.status === ORDER_STATUS.ORDER_SENT_TO_HQ).length;
        const delivered = compiledOrders.filter((o) => o.status === ORDER_STATUS.HQ_DELIVERED).length;
        return { sent, delivered };
    }, [compiledOrders]);

    // ✅ filter ikut tab
    const filteredOrders = useMemo(() => {
        const base = compiledOrders.slice().sort((a, b) => {
            const dateA = a.orderDate?.toDate?.() || new Date(0);
            const dateB = b.orderDate?.toDate?.() || new Date(0);
            return dateB - dateA;
        });

        if (activeTab === TAB_SENT) {
            return base.filter((o) => o.status === ORDER_STATUS.ORDER_SENT_TO_HQ);
        }
        if (activeTab === TAB_DELIVERED) {
            return base.filter((o) => o.status === ORDER_STATUS.HQ_DELIVERED);
        }
        return base;
    }, [compiledOrders, activeTab]);

    return (
        <div className="order-compiled-container">
            <h2>{userRole === "pengerusi" ? "Tempahan yang Telah Dihantar" : "Senarai Tempahan (Pengarah)"}</h2>

            {/* ✅ Tabs */}
            <div className="status-tabs">
                <button
                    className={`tab-btn ${activeTab === TAB_SENT ? "active" : ""}`}
                    onClick={() => setActiveTab(TAB_SENT)}
                    type="button"
                >
                    Untuk Dihantar ({tabCounts.sent})
                </button>

                <button
                    className={`tab-btn ${activeTab === TAB_DELIVERED ? "active" : ""}`}
                    onClick={() => setActiveTab(TAB_DELIVERED)}
                    type="button"
                >
                    Telah Dihantar ({tabCounts.delivered})
                </button>
            </div>

            {filteredOrders.length === 0 ? (
                <p>Tiada rekod untuk tab ini.</p>
            ) : (
                <table className="order-table">
                    <thead>
                        <tr>
                            {userRole !== "pengerusi" && <th>District</th>}
                            <th>Tarikh</th>
                            <th>No. Rujukan</th>
                            <th>Status</th>
                            <th>Barang & Kuantiti</th>
                            <th>Invois</th>
                            {userRole === "pengarah" && <th>Aksi</th>}
                        </tr>
                    </thead>

                    <tbody>
                        {filteredOrders.map((order) => {
                            const isOpen = openRowId === order.id;
                            const summary = getItemsSummary(order.items);

                            return (
                                <tr key={order.id}>
                                    {userRole !== "pengerusi" && <td>{order.district || "-"}</td>}
                                    <td>{formatDateMsMY(order.orderDate)}</td>
                                    <td>{order.orderNumber || "-"}</td>
                                    <td>{order.status || "-"}</td>

                                    {/* ✅ Accordion cell */}
                                    <td>
                                        <div className="items-summary-row">
                                            <span>
                                                📦 {summary.categories} kategori • {summary.products} barang
                                            </span>

                                            <button
                                                className="items-toggle-btn"
                                                type="button"
                                                onClick={() => toggleRow(order.id)}
                                            >
                                                {isOpen ? "Tutup" : "Lihat"}
                                            </button>
                                        </div>

                                        {isOpen && (
                                            <div className="items-accordion">
                                                {order.items &&
                                                    Object.entries(order.items).map(([category, products]) => (
                                                        <div key={category} className="items-category">
                                                            <div className="items-category-title">{category}</div>

                                                            <ul className="items-list">
                                                                {products &&
                                                                    Object.entries(products).map(([productName, details]) => (
                                                                        <li key={productName} className="items-li">
                                                                            <span className="items-product">{productName}</span>{" "}
                                                                            <span className="items-qty">
                                                                                —{" "}
                                                                                {typeof details === "object"
                                                                                    ? Object.entries(details)
                                                                                        .map(([key, qty]) => {
                                                                                            if (key === "Jumlah" && typeof qty === "object") {
                                                                                                return `Jumlah: ${qty.total ?? qty.ordered ?? 0} unit`;
                                                                                            }
                                                                                            if (typeof qty === "object") {
                                                                                                return `${key}: ${qty.total ?? qty.ordered ?? 0} unit`;
                                                                                            }
                                                                                            return `${key}: ${qty} unit`;
                                                                                        })
                                                                                        .join(", ")
                                                                                    : `Jumlah: ${details} unit`}
                                                                            </span>
                                                                        </li>
                                                                    ))}
                                                            </ul>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </td>

                                    <td>
                                        <button className="invoice-button" onClick={() => downloadInvoice(order)} type="button">
                                            PDF
                                        </button>
                                    </td>

                                    {/* ✅ Aksi: sentiasa ada untuk pengarah (button / label) */}
                                    {userRole === "pengarah" && (
                                        <td>
                                            {order.status === ORDER_STATUS.ORDER_SENT_TO_HQ ? (
                                                <button
                                                    className="deliver-button"
                                                    onClick={() => markAsDelivered(order)}
                                                    type="button"
                                                >
                                                    Hantar Barang
                                                </button>
                                            ) : order.status === ORDER_STATUS.HQ_DELIVERED ? (
                                                <span className="delivered-label">Telah Dihantar</span>
                                            ) : (
                                                <span className="pending-label">-</span>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default OrderListCompiled;
